// client/src/pages/seo/QrCodeSpeisekarteDruckenDe.jsx
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import SeoHeader from "../../components/seo/SeoHeader.jsx";

const sections = [
  { id: "before", label: "Was vor dem Druck feststehen muss" },
  { id: "materials", label: "Tischaufsteller, Karten, Aufkleber oder Halter?" },
  { id: "design", label: "Größe, Kontrast und Lesbarkeit" },
  { id: "placement", label: "Wo sollte der QR-Code platziert werden?" },
  { id: "test", label: "Druck-Checkliste vor der Bestellung" },
  { id: "faq", label: "FAQ zum QR-Code-Druck" },
];

function setMeta(name, content) {
  let tag = document.head.querySelector(`meta[name="${name}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("name", name);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function setCanonical(href) {
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}

export default function QrCodeSpeisekarteDruckenDe() {
  useEffect(() => {
    document.documentElement.setAttribute("lang", "de");
    document.title = "QR-Code für Speisekarte drucken: Größe & Platzierung | Best Place";
    setMeta(
      "description",
      "QR-Code für die Speisekarte drucken: praktische Hinweise zu Tischaufstellern, Karten, Aufklebern, Kontrast, Platzierung und Scan-Test vor dem Druck."
    );
    setCanonical("https://best-place.club/de/qr-code-speisekarte-drucken");
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <main className="qrArticle">
      <SeoHeader
        lang="de"
        langLinks={{
          en: "/en/qr-code-menu-printing",
          de: "/de/qr-code-speisekarte-drucken",
          no: "/no/qr-kode-meny-utskrift",
        }}
      />

      <section className="qrArticle__hero">
        <div className="qrArticle__heroInner">
          <h1 className="qrArticle__heroTitle">
            <span>QR-Code für die Speisekarte drucken: Material, Größe und Platzierung</span>
          </h1>
          <div className="qrArticle__topsection">
            <div className="qrArticle__topsection--img-wrapper">
              <div className="qrArticle__topsection--img"></div>
            </div>
            <div className="qrArticle__topsection--text-wrapper">
              <p className="seoHero__lead">
                Bevor Tischaufsteller, Karten oder Aufkleber bestellt werden, sollte
                die digitale Zielseite fertig sein und der QR-Code an echten
                Smartphones zuverlässig funktionieren.
              </p>
              <p className="seoHero__text">
                Diese Seite behandelt ausschließlich den physischen Einsatz des
                QR-Codes: Druckmaterial, Lesbarkeit, Position und Test. Für die
                digitale QR-Speisekarte selbst gibt es unsere Hauptseite.
              </p>
              <p><Link to="/de/qr-code-speisekarte">Zur QR-Code-Speisekarte für Restaurants</Link></p>
            </div>
          </div>
        </div>
      </section>

      <section className="articls">
        <h2>Inhalt</h2>
        <nav className="qrArticle__nav" aria-label="Artikelnavigation">
          <ol className="qrArticle__navList">
            {sections.map((item, index) => (
              <li key={item.id} className="qrArticle__navItem">
                <button type="button" className="qrArticle__navButton" onClick={() => scrollToSection(item.id)}>
                  <span className="qrArticle__navNumber">{String(index + 1).padStart(2, "0")}</span>
                  <span className="qrArticle__navLabel">{item.label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      <article>
        <section id="before" className="acquaintance">
          <h2>Was vor dem Druck feststehen muss</h2>
          <p>
            Der häufigste Fehler besteht darin, schöne Druckmaterialien zu bestellen,
            bevor klar ist, wohin der QR-Code führt. Zuerst sollten Menüseite,
            Ziel-URL und Gästeansicht funktionieren. Erst danach lohnt sich die
            Produktion größerer Stückzahlen.
          </p>
          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Die Menü-URL ist stabil und öffentlich erreichbar.</li>
            <li className="text__seo-li">Das Menü funktioniert auf mehreren Smartphones.</li>
            <li className="text__seo-li">Der QR-Code wurde aus dem finalen Design getestet.</li>
            <li className="text__seo-li">Bei Inhaltsänderungen muss der gedruckte Code nicht ersetzt werden.</li>
          </ul>
        </section>

        <section id="materials" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Tischaufsteller, Karten, Aufkleber oder Halter?</h2>
            <div className="qrArticle__cardGrid">
              <div>
                <strong>Tischaufsteller</strong>
                <p>Gut sichtbar und leicht auszutauschen. Praktisch für Restaurants mit festen Tischen.</p>
              </div>
              <div>
                <strong>Kleine Tischkarte</strong>
                <p>Günstig für Tests und saisonale Änderungen, bevor ein dauerhaftes Material gewählt wird.</p>
              </div>
              <div>
                <strong>Aufkleber</strong>
                <p>Platzsparend, aber weniger flexibel, wenn Design oder Position später geändert werden sollen.</p>
              </div>
              <div>
                <strong>Holz- oder 3D-Halter</strong>
                <p>Wirkt hochwertig, sollte aber erst nach einem erfolgreichen Praxistest produziert werden.</p>
              </div>
            </div>
            <p>
              Für das spezielle Thema Tischaufsteller gibt es zusätzlich die Seite{" "}
              <Link to="/de/restaurant-qr-code-tischaufsteller">Restaurant QR-Code Tischaufsteller</Link>.
            </p>
          </div>
        </section>

        <section id="design" className="acquaintance">
          <h2>Größe, Kontrast und Lesbarkeit</h2>
          <p>
            QR-Codes sollten nicht am theoretischen Minimum gestaltet werden. Im
            Restaurant verändern Abstand, Licht, Reflexionen, Kameras und Winkel die
            Scan-Situation. Ein etwas größerer, kontrastreicher Code ist in der Praxis
            meist robuster als ein sehr kleiner, dekorativer Code.
          </p>
          <ul className="qrArticle__block-li">
            <li className="text__seo-li">hoher Kontrast zwischen Code und Hintergrund</li>
            <li className="text__seo-li">ausreichend freie Fläche um den Code</li>
            <li className="text__seo-li">keine starken Reflexionen über dem Code</li>
            <li className="text__seo-li">Text daneben, der erklärt, was sich öffnet</li>
            <li className="text__seo-li">Scan-Test direkt aus der finalen Druckdatei</li>
          </ul>
          <p>
            Ein klarer Hinweis wie „Speisekarte scannen“ oder „Menü ohne App öffnen“
            nimmt Gästen die Unsicherheit, was nach dem Scan passiert.
          </p>
        </section>

        <section id="placement" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Wo sollte der QR-Code platziert werden?</h2>
            <p>
              Der beste Ort ist dort, wo der Gast die Speisekarte tatsächlich
              braucht. Auf einem Restauranttisch ist das meist gut sichtbar, ohne
              den Gast aktiv danach suchen zu lassen.
            </p>
            <ul className="qrArticle__block-li">
              <li className="text__seo-li">Tischaufsteller oder Tischkarte</li>
              <li className="text__seo-li">Theke oder Selbstbedienungsbereich</li>
              <li className="text__seo-li">Fenster oder Eingang für Vorab-Informationen</li>
              <li className="text__seo-li">Flyer und Take-away-Verpackungen für späteren Zugriff</li>
            </ul>
            <p>
              Vermeiden Sie Positionen, die ständig von Gläsern, Speisen oder
              Dekoration verdeckt werden.
            </p>
          </div>
        </section>

        <section id="test" className="acquaintance">
          <h2>Druck-Checkliste vor der Bestellung</h2>
          <ol className="qrArticle__block-li">
            <li className="text__seo-li">Finale Menü-URL öffnen und prüfen.</li>
            <li className="text__seo-li">QR-Code aus der finalen Druckdatei scannen.</li>
            <li className="text__seo-li">Mit iPhone und Android testen.</li>
            <li className="text__seo-li">Aus typischem Sitzabstand testen.</li>
            <li className="text__seo-li">Bei normalem Restaurantlicht testen.</li>
            <li className="text__seo-li">Zuerst kleine Testauflage produzieren.</li>
          </ol>
          <p>
            Erst wenn diese Punkte funktionieren, sollten größere Mengen bestellt
            oder aufwendige Halter produziert werden.
          </p>
        </section>

        <section id="faq" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>FAQ zum QR-Code-Druck</h2>
            <div className="seoFaq">
              <details>
                <summary>Muss ich den QR-Code bei jeder Menüänderung neu drucken?</summary>
                <p>Nein, wenn der Code auf eine stabile Menü-URL verweist und nur die Inhalte dahinter geändert werden.</p>
              </details>
              <details>
                <summary>Was ist besser: Aufkleber oder Tischaufsteller?</summary>
                <p>Tischaufsteller und austauschbare Karten sind flexibler. Aufkleber sind dauerhaft, aber später schwerer zu ändern.</p>
              </details>
              <details>
                <summary>Kann ich einen 3D-gedruckten Halter verwenden?</summary>
                <p>Ja. Sinnvoll ist ein Halter für eine austauschbare Karte mit sauber gedrucktem QR-Code.</p>
              </details>
              <details>
                <summary>Sollte ich Papier-Speisekarten sofort entfernen?</summary>
                <p>Nicht zwingend. Viele Restaurants nutzen digitale und gedruckte Karten parallel.</p>
              </details>
            </div>
          </div>
        </section>

        <section className="articls qrArticle__related">
          <h2>Weiterführend</h2>
          <div className="seoCompareSmall">
            <p><Link to="/de/qr-code-speisekarte">QR-Code-Speisekarte für Restaurants</Link></p>
            <p><Link to="/de/restaurant-qr-code-tischaufsteller">QR-Code-Tischaufsteller</Link></p>
            <p><Link to="/de/qr-code-speisekarte-ohne-app">QR-Speisekarte ohne App</Link></p>
          </div>
        </section>
      </article>
    </main>
  );
}
